import { Box } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import {
  LwButton,
  MultiDateCalendar,
  StyledActionsBox,
  StyledContentBox,
  StyledDivider,
  StyledForm,
  StyledTitle,
} from 'redesign';
import { useCallback, useMemo } from 'react';
import { useCalendarDefaults } from 'shared/hooks/useCalendarDefaults';
import { useCalendar } from 'shared/hooks/use-calendar';
import { FilterEmploymentType } from 'shared/components/filters/inputs/employment-type/filter-employment-type';
import { FilterProps, InboxFormValues } from './inbox-filter-form.types';
import { BusinessUnitFilteringLegacy } from './business-unit-filtering-legacy';
import { getCheckoutDefaultFilterValues } from './use-filter-default-values';
import { InboxFiltersParams } from '../../helpers/checkouts.types';

export const InboxFilterForm = ({
  mode,
  activeFilters,
  applyFilters,
  onCancel,
  clear,
  data,
}: FilterProps<InboxFiltersParams>) => {
  const { monthLabels, dayLabels } = useCalendarDefaults();
  const maxDate = useMemo(() => new Date(), []);

  const defaultFilterValues = useMemo(() => {
    return getCheckoutDefaultFilterValues({ branchOffices: data.branchOffices, activeFilters });
  }, [data.branchOffices, activeFilters]);

  const { dateRange, handleDateChange, getDateRangeFrom, handleResetDateRange } = useCalendar({
    fromDate: activeFilters.fromDate,
    toDate: activeFilters.toDate,
  });

  const form = useForm<InboxFormValues>({
    defaultValues: {
      ...activeFilters,
      flexWorkers: activeFilters.flexWorkerIds?.map((flexWorkerId) => {
        const flexWorker = data.flexWorkers.find((fw) => fw.id === flexWorkerId);
        return { name: flexWorker?.fullName ?? '', id: flexWorkerId };
      }),
      branchOffice: {
        label: defaultFilterValues.defaultBranchOffice?.label,
        value: defaultFilterValues.defaultBranchOffice?.value,
      },
      department: {
        label: defaultFilterValues.defaultDepartment?.label,
        value: defaultFilterValues.defaultDepartment?.value,
      },
      employmentTypeCheckboxValue:
        activeFilters.isFreelance !== undefined ||
        activeFilters.isTempWork !== undefined ||
        activeFilters.isEmployee !== undefined,
    },
  });

  const onSubmit = useCallback(
    (formData: InboxFormValues) => {
      let departmentIds = [];

      if (formData.department === null || formData.department.value === undefined) {
        const foundBranchOffice = data.branchOffices.find(
          (bo: any) => bo.id === formData.branchOffice?.value
        );
        departmentIds = foundBranchOffice?.departments.map((dep) => dep.id) ?? [];
      } else {
        departmentIds = [formData.department.value];
      }

      const submitData: InboxFiltersParams = {
        branchOffice: formData.branchOffice,
        departmentIds,
        flexWorkerIds: formData.flexWorkers?.map((flexWorker) => flexWorker.id),
        fromDate: dateRange.fromDate,
        toDate: dateRange.toDate,
        isFreelance: formData.employmentTypeCheckboxValue ? formData.isFreelance : undefined,
        isTempWork: formData.employmentTypeCheckboxValue ? formData.isTempWork : undefined,
        isEmployee: formData.employmentTypeCheckboxValue ? formData.isEmployee : undefined,
        filterByPrio: formData.filterByPrio,
      };

      applyFilters(submitData);
      onCancel();
    },
    [applyFilters, data.branchOffices, dateRange.fromDate, dateRange.toDate, onCancel]
  );

  const clearForm = useCallback(() => {
    clear();
    form.reset({
      flexWorkers: null,
      branchOffice: null,
      department: null,
    });
    handleResetDateRange();
  }, [clear, form, handleResetDateRange]);

  return (
    <FormProvider {...form}>
      <StyledContentBox>
        <StyledTitle variant="h2">Filter Checkouts</StyledTitle>
        <StyledDivider />
        <StyledForm onSubmit={form.handleSubmit(onSubmit)}>
          <Box>
            <MultiDateCalendar
              weekStartDayIndex={1}
              range
              months={monthLabels}
              weekDays={dayLabels}
              value={getDateRangeFrom(dateRange.fromDate, dateRange.toDate)}
              maxDate={maxDate}
              onChange={handleDateChange}
            />
            <Box flex="1" justifyContent="flex-end" marginBottom={6}>
              <LwButton color="secondary" onClick={handleResetDateRange}>
                Datumselectie wissen
              </LwButton>
            </Box>
            <BusinessUnitFilteringLegacy {...{ applyFilters, onCancel, clear }} data={data} />
            <FilterEmploymentType hasEmployee={mode === 'shifts'} />
          </Box>
          <StyledActionsBox>
            <Box flexBasis="100%">
              <LwButton color="primary" type="submit">
                Filter
              </LwButton>
            </Box>
            <LwButton color="secondary" onClick={onCancel}>
              Terug
            </LwButton>
            <LwButton color="secondary" onClick={clearForm}>
              Filters wissen
            </LwButton>
          </StyledActionsBox>
        </StyledForm>
      </StyledContentBox>
    </FormProvider>
  );
};
